
import React from 'react';
import { Key, Mail } from 'lucide-react';
import { TabsContent } from '@/components/ui/tabs';
import { CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from './FormField';
import { AuthButton } from './AuthButton';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface ResetPasswordTabProps {
  email: string;
  setEmail: (email: string) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  success: string | null;
  setSuccess: (success: string | null) => void;
  resetErrors: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const ResetPasswordTab = ({
  email,
  setEmail,
  loading,
  setLoading,
  success,
  setSuccess,
  resetErrors,
  onKeyDown
}: ResetPasswordTabProps) => {
  const { resetPassword } = useAuth();
  const { toast } = useToast();

  const handleResetPassword = async () => {
    if (!email) {
      toast({
        title: 'Erreur',
        description: 'Veuillez entrer votre email',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    resetErrors();
    
    try {
      const { error } = await resetPassword(email);
      if (error) {
        toast({
          title: 'Erreur',
          description: error.message || 'Une erreur est survenue',
          variant: 'destructive',
        });
      } else {
        setSuccess('Email de réinitialisation envoyé ! Vérifiez votre boîte de réception.');
        toast({
          title: 'Email envoyé',
          description: 'Vérifiez votre email pour réinitialiser votre mot de passe',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Une erreur est survenue',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TabsContent value="reset" className="animate-fade-in">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl font-bold text-gray-800 dark:text-white">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-youtube-red to-youtube-red-dark">Mot de passe oublié ?</span>
        </CardTitle>
        <CardDescription className="text-gray-500 dark:text-gray-400">Nous vous enverrons un lien pour réinitialiser votre mot de passe</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <FormField
          id="reset-email"
          type="email"
          placeholder="email@exemple.fr"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={onKeyDown}
          icon={<Mail className="w-4 h-4 mr-2 text-youtube-red-light" />}
          label="Email"
        />
        {success && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-md text-sm text-green-800 dark:text-green-400 animate-fade-in">
            {success}
          </div>
        )}
      </CardContent>
      <CardFooter className="pb-6">
        <AuthButton
          id="reset-button"
          onClick={handleResetPassword}
          loading={loading}
          icon={<Key className="w-5 h-5" />}
          text="Réinitialiser le mot de passe"
          loadingText="Envoi..."
        />
      </CardFooter>
    </TabsContent>
  );
};
